import styled, { css } from 'styled-components';
import {
  U21FormLabel,
  U21Divider,
  U21Spacer,
  U21Typography,
  U21HelpTooltip,
  U21Button,
} from 'app/shared/u21-ui/components';
import { HTMLProps, ReactNode } from 'react';
import { getDOMProps } from 'app/shared/utils/react';
import { IconTrash } from '@u21/tabler-icons';
import { SarDeleteButton } from 'app/modules/fincenSAR/components/SarDeleteButton';

// Block 1
export const Block1 = ({
  action,
  children,
  hasDivider,
  title,
  hasMarginTop = true,
}: {
  action?: ReactNode;
  children: ReactNode;
  hasDivider?: boolean;
  title?: ReactNode;
  hasMarginTop?: boolean;
}) => {
  return (
    <StyledBlock1Div $hasMarginTop={hasMarginTop}>
      {hasDivider && <U21Divider style={{ marginBottom: '2rem' }} horizontal />}
      {title && (
        <Block1Header>
          <U21Typography variant="h4">{title}</U21Typography>
          {action}
        </Block1Header>
      )}
      {children}
    </StyledBlock1Div>
  );
};
interface StyledBlock1DivProps {
  $hasMarginTop: boolean;
}
const StyledBlock1Div = styled.div<StyledBlock1DivProps>`
  ${({ $hasMarginTop }) => {
    if ($hasMarginTop) {
      return css`
        margin-top: 3rem;
      `;
    }
    return css``;
  }}
`;
const Block1Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

// Block 2
interface Block2Props {
  $marginTop?: string;
}

export const Block2 = styled.div<Block2Props>`
  display: flex;
  align-items: start;
  gap: 1rem;
  ${({ $marginTop }) => {
    if ($marginTop) {
      return css`
        margin-top: ${$marginTop};
      `;
    }
    return css`
      margin-top: 2rem;
    `;
  }}
`;

export const GroupContainer = styled(U21Spacer)`
  flex: 1;
`;

export const GroupItemContainer = styled(U21Spacer).attrs({
  spacing: 2,
})`
  flex: 1;
`;

export const Block2Heading = ({
  children,
  description,
  $marginTop = '0',
}: {
  children: ReactNode;
  description?: string;
  $marginTop?: string;
}) => {
  return (
    <div>
      <U21Typography style={{ marginTop: $marginTop }} variant="h6">
        {children}
      </U21Typography>
      {description && (
        <U21Typography color="text.secondary" variant="body2">
          {description}
        </U21Typography>
      )}
    </div>
  );
};

interface Block2HeaderProps {
  $justifyContent?: string;
}

export const Block2Header = styled.div<Block2HeaderProps>`
  display: flex;
  margin-top: 1rem;
  ${({ $justifyContent }) => {
    if ($justifyContent) {
      return css`
        justify-content: ${$justifyContent};
      `;
    }
    return css`
      justify-content: space-between;
    `;
  }}
`;

export const Block2Main2Field = styled.div`
  flex: 2;
`;

export const Block2MainField = styled.div`
  flex: 1;
`;

export const Block2Secondary = styled.div`
  display: flex;
`;

interface Block2AddActionsProps {
  $justifyContent?: string;
  $marginTop?: string;
  children: ReactNode;
  disabled?: boolean;
}

export const Block2AddActions = ({
  children,
  $justifyContent,
  $marginTop,
  disabled,
}: Block2AddActionsProps) => {
  if (disabled) {
    return null;
  }
  return (
    <Block2AddActionsDiv
      $justifyContent={$justifyContent}
      $marginTop={$marginTop}
    >
      {children}
    </Block2AddActionsDiv>
  );
};

interface Block2AddActionsPropsDiv {
  $justifyContent?: string;
  $marginTop?: string;
}

const Block2AddActionsDiv = styled.div<Block2AddActionsPropsDiv>`
  display: flex;
  ${({ $justifyContent }) => {
    if ($justifyContent) {
      return css`
        justify-content: ${$justifyContent};
      `;
    }
    return css`
      justify-content: end;
    `;
  }}
  ${({ $marginTop }) => {
    if ($marginTop) {
      return css`
        margin-top: ${$marginTop};
      `;
    }
    return css`
      margin-top: 2rem;
    `;
  }}
`;

// Labels
interface FieldLabelProps extends HTMLProps<HTMLLabelElement> {
  children: ReactNode;
  description?: string;
  required?: boolean;
  helpText?: ReactNode;
}

export const FieldLabel = ({
  children,
  description,
  required,
  helpText,
  ...rest
}: FieldLabelProps) => {
  return (
    <StyledLabelDiv>
      <StyledLabel required={required} {...getDOMProps(rest)}>
        {children}
      </StyledLabel>
      {helpText && <StyledU21HelpTooltip help={helpText} />}
      {description && (
        <U21Typography color="text.secondary" variant="body2">
          {description}
        </U21Typography>
      )}
    </StyledLabelDiv>
  );
};
const StyledLabelDiv = styled.div`
  width: 25%;
  margin-top: 8px;
`;
const StyledLabel = styled(U21FormLabel)`
  display: inline;
`;
export const StyledU21HelpTooltip = styled(U21HelpTooltip)`
  margin-top: -2px;
`;

export const InlineFieldLabel = styled(U21FormLabel)`
  display: inline;
  margin-top: 8px;
  margin-right: 0.5rem;
`;

// Misc
interface HideableDivProps {
  $hidden: boolean;
}

export const HideableDiv = styled.div<HideableDivProps>`
  ${(props) =>
    props.$hidden
      ? css`
          visibility: hidden;
        `
      : css``};
`;

export const SarIconTrash = () => <IconTrash stroke={1.5} />;

interface ArrayBlockProps {
  children: ReactNode;
  arrayLength?: number;
  arrayIdx?: number;
  arrayPrefix: string;
  lastElmPrefix: string;
  deleteAriaLabel: string;
  removeSection: () => void;
}

export const ArrayBlock = ({
  children,
  arrayLength,
  arrayIdx,
  deleteAriaLabel,
  removeSection,
  arrayPrefix,
  lastElmPrefix,
}: ArrayBlockProps) => {
  return (
    <>
      {arrayLength && arrayLength > 1 && (
        <Block2Header $justifyContent="end">
          <SarDeleteButton
            variant="ghost"
            startIcon={<SarIconTrash />}
            aria-label={deleteAriaLabel}
            onClick={removeSection}
            arrayPrefix={arrayPrefix}
            lastElmPrefix={lastElmPrefix}
          >
            Delete Section
          </SarDeleteButton>
        </Block2Header>
      )}

      {children}
      {arrayLength && arrayLength - 1 !== arrayIdx && (
        <U21Divider
          horizontal
          style={{
            borderStyle: 'dashed',
            margin: '2rem 4rem 0 4rem',
          }}
        />
      )}
    </>
  );
};

export const ActionsContainer = ({
  children,
  $justifyContent,
  $width,
}: {
  children: ReactNode;
  $justifyContent?: string;
  $width?: string;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        width: $width || '6.5rem',
        justifyContent: $justifyContent || 'start',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  );
};

export const HelpUl = styled.ul`
  margin-left: 1rem;
`;

export const HelpLinkButton = styled(U21Button)`
  white-space: break-spaces;
  word-break: break-all;
`;
